import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ShieldCheck, CheckCircle, Star, MapPin, Video, HouseSimple, UsersThree,
  ArrowLeft, Sparkle, Clock, Globe, IdentificationBadge, ChatCircleDots, Calendar,
  Certificate, GraduationCap,
} from '@phosphor-icons/react';
import Header from '@/components/Header';
import PremiumFooter from '@/components/PremiumFooter';
import BookingModal from '@/components/wellness/BookingModal';
import ConsultationRequestModal from '@/components/wellness/ConsultationRequestModal';
import { MODES, formatPrice, getCategoryById } from '@/data/wellness';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MODE_ICON = { online: Video, in_person: UsersThree, home_visit: HouseSimple };

const WellnessProfilePage = ({ user }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pro, setPro] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [verifiedCreds, setVerifiedCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [consultationOpen, setConsultationOpen] = useState(false);

  const fetchPro = useCallback(async () => {
    try {
      const [pRes, rRes, cRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/wellness/professionals/${slug}`),
        axios.get(`${BACKEND_URL}/api/wellness/professionals/${slug}/reviews`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/wellness/pro/${slug}/verified-credentials`).catch(() => ({ data: [] })),
      ]);
      setPro(pRes.data);
      setReviews(rRes.data);
      setVerifiedCreds(cRes.data);
    } catch (err) {
      toast.error('Professional not found or not yet approved');
      setPro(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchPro(); }, [fetchPro]);

  useEffect(() => {
    if (pro && searchParams.get('book') === '1') {
      setBookingOpen(true);
      searchParams.delete('book');
      setSearchParams(searchParams, { replace: true });
    }
  }, [pro, searchParams, setSearchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Header user={user} />
        <div className="flex items-center justify-center py-32">
          <div className="w-14 h-14 border-4 border-[#1A4331] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!pro) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Header user={user} />
        <div className="max-w-3xl mx-auto text-center py-24 px-6">
          <h1 className="text-3xl font-semibold mb-2">Professional not found</h1>
          <p className="text-stone-600 mb-6">This profile may not be approved yet, or the link is invalid.</p>
          <button onClick={() => navigate('/wellness/find')} className="bg-[#1A4331] text-white px-6 py-3 rounded-full font-semibold">Browse Experts</button>
        </div>
      </div>
    );
  }

  const category = getCategoryById(pro.category_id);
  const Icon = category?.icon;

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="wellness-pro-profile">
      <Header user={user} />

      {/* Hero */}
      <section className={`relative bg-gradient-to-br ${category?.tint || 'from-stone-100 to-stone-200'} px-6 py-12 md:py-16 overflow-hidden`}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.08) 1px, transparent 0)', backgroundSize: '18px 18px' }} />
        <div className="relative max-w-6xl mx-auto">
          <button onClick={() => navigate('/wellness/find')} className="text-sm text-stone-600 hover:text-[#1A4331] mb-6 inline-flex items-center gap-1">
            <ArrowLeft size={14} weight="bold" /> Back to results
          </button>

          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-start">
            {/* Avatar */}
            <div className="w-28 h-28 rounded-3xl bg-white shadow-xl border-4 border-white flex items-center justify-center overflow-hidden flex-shrink-0">
              {pro.hero_image_url ? (
                <img src={pro.hero_image_url} alt={pro.full_name} className="w-full h-full object-cover" />
              ) : (
                Icon && <Icon size={48} weight="duotone" style={{ color: category.color }} />
              )}
            </div>

            {/* Info */}
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 bg-white/70 px-2 py-0.5 rounded-full">
                  {category?.name}
                </span>
                {pro.is_top_professional && (
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] bg-[#F59E0B]/15 text-[#78350F] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Star size={10} weight="fill" /> Top
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-semibold text-[#1C1917] font-heading tracking-tight" data-testid="pro-full-name">{pro.full_name}</h1>
              <p className="text-lg text-stone-600 mt-1">{pro.professional_type}</p>
              {pro.headline && <p className="text-stone-700 mt-3 max-w-xl">{pro.headline}</p>}

              <div className="flex flex-wrap gap-2 mt-5">
                {pro.nova_verified && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold bg-[#F0F7F1] text-[#1A4331] px-3 py-1 rounded-full">
                    <ShieldCheck size={14} weight="fill" /> Nova Verified
                  </span>
                )}
                {pro.credential_verified && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold bg-[#ECFDF5] text-[#065F46] px-3 py-1 rounded-full">
                    <CheckCircle size={14} weight="fill" /> Credentials Verified
                  </span>
                )}
                {pro.identity_verified && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold bg-[#EFF6FF] text-[#1E40AF] px-3 py-1 rounded-full">
                    <IdentificationBadge size={14} weight="fill" /> Identity Verified
                  </span>
                )}
              </div>

              {/* Verified Credentials Badge Shelf */}
              {verifiedCreds.length > 0 && (
                <div className="mt-5 p-4 bg-emerald-50/70 border border-emerald-100 rounded-2xl">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={13} weight="fill" className="text-white" />
                    </div>
                    <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Verified Credentials</p>
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Nova Verified</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {verifiedCreds.map(cred => (
                      <div key={cred.credential_id}
                        className="flex items-center gap-2 bg-white border border-emerald-200 rounded-xl px-3 py-2 shadow-sm"
                        data-testid={`verified-cred-${cred.credential_id}`}>
                        <GraduationCap size={15} weight="fill" className="text-emerald-600 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-[#1C1917] leading-tight">{cred.title}</p>
                          {(cred.institution || cred.year) && (
                            <p className="text-[10px] text-stone-400 leading-tight mt-0.5">
                              {cred.institution}{cred.institution && cred.year ? ' · ' : ''}{cred.year}
                            </p>
                          )}
                        </div>
                        <CheckCircle size={13} weight="fill" className="text-emerald-500 flex-shrink-0 ml-1" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4 mt-5 text-sm text-stone-600">
                {pro.avg_rating > 0 && (
                  <span className="inline-flex items-center gap-1"><Star size={14} weight="fill" className="text-[#F59E0B]" /> {pro.avg_rating.toFixed(1)}</span>
                )}
                <span className="inline-flex items-center gap-1"><MapPin size={14} weight="fill" /> {pro.city}, {pro.country}</span>
                {pro.years_experience > 0 && <span className="inline-flex items-center gap-1"><Clock size={14} weight="fill" /> {pro.years_experience} years experience</span>}
                {pro.total_bookings > 0 && <span>{pro.total_bookings} sessions completed</span>}
              </div>
            </div>

            {/* Book CTA sidebar */}
            <div className="md:min-w-[260px]">
              <div className="bg-white rounded-3xl p-6 border border-stone-100 shadow-xl">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-400">Starting from</p>
                <p className="text-3xl font-semibold text-[#1A4331] font-heading tracking-tight" data-testid="pro-price">
                  {pro.price_from > 0 ? formatPrice(pro.price_from, pro.currency) : 'On request'}
                  <span className="text-sm text-stone-400 font-normal ml-1">/ session</span>
                </p>
                <button
                  onClick={() => setBookingOpen(true)}
                  className="w-full mt-5 bg-[#1A4331] text-white py-3 rounded-full font-bold hover:bg-[#2A5A44] transition-all shadow-md flex items-center justify-center gap-2"
                  data-testid="book-consultation-btn"
                >
                  <Calendar size={18} weight="duotone" /> Book Consultation
                </button>
                <button onClick={() => setConsultationOpen(true)} className="w-full mt-2 bg-white border border-stone-200 text-stone-700 py-3 rounded-full font-semibold hover:border-[#1A4331] transition-all flex items-center justify-center gap-2" data-testid="send-request-btn">
                  <ChatCircleDots size={18} weight="duotone" /> Send Request
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="md:col-span-2 space-y-8">
            {/* About */}
            <div className="bg-white rounded-3xl p-7 border border-stone-100">
              <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500 mb-3">About</h2>
              <p className="text-stone-700 leading-relaxed whitespace-pre-line">
                {pro.bio || `${pro.full_name} is a ${pro.professional_type.toLowerCase()} based in ${pro.city}. Book a consultation to learn more about their approach and services.`}
              </p>
            </div>

            {/* Services */}
            <div className="bg-white rounded-3xl p-7 border border-stone-100">
              <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500 mb-4">Consultation</h2>
              <div className="border border-stone-100 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-lg font-semibold text-[#1C1917]">{pro.professional_type} — 1:1 Consultation</p>
                    <p className="text-sm text-stone-500 mt-1">Personalized guidance based on your goals</p>
                    <div className="flex flex-wrap gap-3 mt-3 text-xs text-stone-600">
                      <span className="inline-flex items-center gap-1"><Clock size={12} weight="fill" /> 45 min</span>
                      {(pro.modes || []).map((m) => {
                        const M = MODE_ICON[m];
                        return (
                          <span key={m} className="inline-flex items-center gap-1">
                            {M && <M size={12} weight="duotone" />} {MODES.find((x) => x.id === m)?.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-semibold text-[#1A4331] font-heading">{pro.price_from > 0 ? formatPrice(pro.price_from, pro.currency) : 'On request'}</p>
                    <button
                      onClick={() => setBookingOpen(true)}
                      className="mt-2 bg-[#1A4331] text-white px-4 py-1.5 rounded-full text-xs font-bold hover:bg-[#2A5A44] transition-all"
                    >
                      Book
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews */}
            {reviews.length > 0 && (
              <div className="bg-white rounded-3xl p-7 border border-stone-100" data-testid="pro-reviews-section">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500">Reviews</h2>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#1C1917]"><Star size={14} weight="fill" className="text-[#F59E0B]" /> {pro.avg_rating.toFixed(1)} ({reviews.length})</span>
                </div>
                <div className="space-y-4">
                  {reviews.slice(0, 5).map((r) => (
                    <div key={r.review_id} className="border-b border-stone-100 last:border-0 pb-4 last:pb-0" data-testid={`review-${r.review_id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-sm text-[#1C1917]">{r.customer_name || 'Customer'}</p>
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={11} weight="fill" className={n <= r.rating ? 'text-[#F59E0B]' : 'text-stone-200'} />)}
                        </div>
                        <span className="text-xs text-stone-400 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      {r.title && <p className="font-semibold text-sm text-[#1C1917] mb-0.5">{r.title}</p>}
                      {r.text && <p className="text-sm text-stone-600 leading-relaxed">{r.text}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="bg-white rounded-3xl p-6 border border-stone-100">
              <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500 mb-3">Why this professional?</h3>
              <ul className="space-y-2.5 text-sm text-stone-700">
                {pro.nova_verified && <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#1A4331]" /> Nova Verified</li>}
                {pro.credential_verified && <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#1A4331]" /> Credential Verified</li>}
                {pro.identity_verified && <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#1A4331]" /> Identity Verified</li>}
                {pro.years_experience > 0 && <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#1A4331]" /> {pro.years_experience} years experience</li>}
                {pro.total_bookings > 0 && <li className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#1A4331]" /> {pro.total_bookings} sessions completed</li>}
                {pro.avg_rating > 0 && <li className="flex items-center gap-2"><Star size={16} weight="fill" className="text-[#F59E0B]" /> {pro.avg_rating.toFixed(1)} average rating</li>}
              </ul>
            </div>

            {(pro.languages && pro.languages.length > 0) && (
              <div className="bg-white rounded-3xl p-6 border border-stone-100">
                <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-stone-500 mb-3 flex items-center gap-2">
                  <Globe size={14} weight="duotone" /> Languages
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {pro.languages.map((lang) => (
                    <span key={lang} className="text-xs bg-stone-50 border border-stone-100 text-stone-700 px-2.5 py-1 rounded-full">{lang}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white rounded-3xl p-6">
              <Sparkle size={20} weight="duotone" className="text-[#34D399] mb-2" />
              <p className="font-semibold mb-1">100% Verified Nova Marketplace</p>
              <p className="text-xs text-stone-200 leading-relaxed">Every professional on Nova is reviewed by our team. No fake credentials, no fake reviews.</p>
            </div>
          </aside>
        </div>
      </section>

      <BookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        pro={pro}
        user={user}
      />

      <ConsultationRequestModal
        open={consultationOpen}
        onClose={() => setConsultationOpen(false)}
        pro={pro}
        user={user}
      />

      <PremiumFooter />
    </div>
  );
};

export default WellnessProfilePage;
